import { ExerciseStateMachine } from './ExerciseStateMachine';

// State enums for the Core Twist exercise
export const CoreTwistState = {
  CENTER: 'CENTER',       // Facing forward in center position
  RIGHT: 'RIGHT',         // Twisted to the right side
  LEFT: 'LEFT',           // Twisted to the left side
};

// Threshold constants
const CENTER_THRESHOLD = 12;      // Angle threshold to detect center position
const TWIST_THRESHOLD = 12;       // Angle threshold to detect twist (equal to center to avoid dead zone)
const MIN_TWIST_ANGLE = 15;       // Minimum angle for good range of motion
const MAX_TWIST_ANGLE = 70;      // Maximum recommended angle (safety)
const MIN_TIME_IN_POSITION = 200; // ms to confirm state (reduced for faster response)
const MIN_TIME_BETWEEN_REPS = 600; // ms minimum time between repetitions (reduced for therapeutic flow)
const INTERNAL_COOLDOWN = 800;  // Internal cooldown to prevent double counting (reduced)

export class CoreTwistFSM extends ExerciseStateMachine {
  constructor() {
    // Initial configuration for the Core Twist FSM
    const config = {
      initialState: CoreTwistState.CENTER,
      initialStateData: {
        currentTwistAngle: 0,
        previousTwistAngle: 0,
        twistHistory: [],
        formScore: 80,
        rangeOfMotionScore: 100,
        controlScore: 100,
        repFormScore: 0,
        exerciseProgress: 0,

        // Tracking variables for rep counting
        lastStateChangeTime: 0,
        lastRepTime: 0,
        maxRightTwistAngle: 0,
        maxLeftTwistAngle: 0,
        maxAbsoluteTwistAngle: 0,   // NEW: Track overall max for ROM calculation
        lastSideCompleted: null,
        visitedStates: ['CENTER'],
        repCounted: false,          // Flag to track if a rep was already counted
        centerReturnTime: 0,        // Time when we last returned to center
        stableInCenter: true,       // Track if we're stable in center
        inInternalCooldown: false,  // Internal cooldown state
        cooldownEndTime: 0          // When internal cooldown ends
      },
      initialFormScores: {
        overall: 100,
        rangeOfMotion: 100,
        control: 100
      },
      transitions: {
        // Transitions from CENTER state
        [CoreTwistState.CENTER]: [
          {
            targetState: CoreTwistState.RIGHT,
            condition: (metrics, stateData, timestamp) => 
              metrics.currentTwistAngle > TWIST_THRESHOLD &&
              timestamp - stateData.lastStateChangeTime > MIN_TIME_IN_POSITION,
            action: (fsm, metrics, timestamp) => {
              fsm.stateData.lastStateChangeTime = timestamp;
              fsm.stateData.visitedStates.push('RIGHT');
              fsm.stateData.stableInCenter = false;
              fsm.stateData.repCounted = false;
              
              // Reset max angles when starting a new twist
              fsm.stateData.maxRightTwistAngle = metrics.currentTwistAngle;
              fsm.stateData.maxAbsoluteTwistAngle = 0;
              
              // console.log("FSM: Transition to RIGHT state");
            }
          },
          {
            targetState: CoreTwistState.LEFT,
            condition: (metrics, stateData, timestamp) => 
              metrics.currentTwistAngle < -TWIST_THRESHOLD &&
              timestamp - stateData.lastStateChangeTime > MIN_TIME_IN_POSITION,
            action: (fsm, metrics, timestamp) => {
              fsm.stateData.lastStateChangeTime = timestamp;
              fsm.stateData.visitedStates.push('LEFT');
              fsm.stateData.stableInCenter = false;
              fsm.stateData.repCounted = false;
              
              // Reset max angles when starting a new twist
              fsm.stateData.maxLeftTwistAngle = metrics.currentTwistAngle;
              fsm.stateData.maxAbsoluteTwistAngle = 0;
              
              // console.log("FSM: Transition to LEFT state");
            }
          }
        ],
        
        // Transitions from RIGHT state
        [CoreTwistState.RIGHT]: [
          {
            targetState: CoreTwistState.CENTER,
            condition: (metrics, stateData, timestamp) => 
              Math.abs(metrics.currentTwistAngle) < CENTER_THRESHOLD &&
              timestamp - stateData.lastStateChangeTime > MIN_TIME_IN_POSITION,
            action: (fsm, metrics, timestamp) => {
              fsm.stateData.lastStateChangeTime = timestamp;
              fsm.stateData.centerReturnTime = timestamp;
              fsm.stateData.stableInCenter = false;
              fsm.stateData.visitedStates.push('CENTER');
              
              // console.log("FSM: Transition from RIGHT to CENTER");
            }
          }
        ],
        
        // Transitions from LEFT state
        [CoreTwistState.LEFT]: [
          {
            targetState: CoreTwistState.CENTER,
            condition: (metrics, stateData, timestamp) => 
              Math.abs(metrics.currentTwistAngle) < CENTER_THRESHOLD &&
              timestamp - stateData.lastStateChangeTime > MIN_TIME_IN_POSITION,
            action: (fsm, metrics, timestamp) => {
              fsm.stateData.lastStateChangeTime = timestamp;
              fsm.stateData.centerReturnTime = timestamp;
              fsm.stateData.stableInCenter = false;
              fsm.stateData.visitedStates.push('CENTER');
              
              // console.log("FSM: Transition from LEFT to CENTER");
            }
          }
        ]
      }
    };
    
    super(config);
    
    // Store the config for reset functionality
    this.constructor.initialConfig = {
      initialState: config.initialState,
      initialStateData: { ...config.initialStateData },
      initialFormScores: { ...config.initialFormScores }
    };
  }
  
  // Override process method to implement strict repetition counting
  process(results, timestamp) {
    // Run the standard FSM processing
    const baseResult = super.process(results, timestamp);
    let isRepCompleted = false;
    
    // Update cooldown status
    if (this.stateData.inInternalCooldown && timestamp > this.stateData.cooldownEndTime) {
      this.stateData.inInternalCooldown = false;
      // console.log("FSM: Internal cooldown ended");
    }
    
    // Only count reps when we're stable in center position after a twist
    const timeInCenter = timestamp - this.stateData.centerReturnTime;
    
    // DEBUG: Log current state periodically (disabled)
    // if (Math.random() < 0.01) {
    //   console.log(`[CoreTwist FSM] State: ${this.currentState}, Angle: ${this.stateData.currentTwistAngle?.toFixed(1)}°`);
    // }
    
    // Check if we should count a rep (we need to be in center position for a while)
    // Reduced time in center for faster therapeutic flow
    if (this.currentState === CoreTwistState.CENTER && 
        !this.stateData.stableInCenter && 
        timeInCenter > 200 && 
        !this.stateData.repCounted &&
        !this.stateData.inInternalCooldown &&
        timestamp - this.stateData.lastRepTime > MIN_TIME_BETWEEN_REPS) {
      
      // Look at the last states visited to determine which side was MOST RECENTLY completed
      let lastSideVisited = null;
      let foundLeft = false;
      let foundRight = false;
      
      // Check last 4 state transitions (reverse order to find most recent non-CENTER state)
      const recentStates = this.stateData.visitedStates.slice(-4);
      for (let i = recentStates.length - 1; i >= 0; i--) {
        const state = recentStates[i];
        if (state === 'LEFT') {
          foundLeft = true;
          if (!lastSideVisited) lastSideVisited = 'left';
        }
        if (state === 'RIGHT') {
          foundRight = true;
          if (!lastSideVisited) lastSideVisited = 'right';
        }
      }
      
      // Determine which side to count based on MOST RECENT twist
      let countSide = null;
      
      // Prioritize the most recently visited side
      if (lastSideVisited === 'right' && this.stateData.maxRightTwistAngle > MIN_TWIST_ANGLE) {
        // Only skip if we JUST counted this exact side very recently
        if (this.stateData.lastSideCompleted !== 'right' || !this.stateData.inInternalCooldown) {
          countSide = 'right';
        } else {
          // console.log(`⚠️ FSM: Right twist detected but skipped - same side counted recently`);
        }
      } else if (lastSideVisited === 'left' && Math.abs(this.stateData.maxLeftTwistAngle) > MIN_TWIST_ANGLE) {
        // Only skip if we JUST counted this exact side very recently  
        if (this.stateData.lastSideCompleted !== 'left' || !this.stateData.inInternalCooldown) {
          countSide = 'left';
        } else {
          // console.log(`⚠️ FSM: Left twist detected but skipped - same side counted recently`);
        }
      }
      
      // Log why rep wasn't counted
      if (!countSide) {
        if (foundRight && this.stateData.maxRightTwistAngle <= MIN_TWIST_ANGLE) {
          // console.log(`⚠️ FSM: Right twist too small`);
        }
        if (foundLeft && Math.abs(this.stateData.maxLeftTwistAngle) <= MIN_TWIST_ANGLE) {
          // console.log(`⚠️ FSM: Left twist too small`);
        }
        if (!foundRight && !foundLeft) {
          // console.log(`⚠️ FSM: No twist detected in recent states`);
        }
      }
      
      // Count rep if valid
      if (countSide) {
        // console.log(`FSM: Counting rep for ${countSide} side...`);
        
        // Increment the actual repetition count
        this.repetitionCount++;
        
        // Calculate form scores for this specific rep
        let rangeScore;
        if (countSide === 'right') {
          rangeScore = Math.min(100, (this.stateData.maxRightTwistAngle / 60) * 100);
          this.stateData.maxRightTwistAngle = 0;
        } else {
          rangeScore = Math.min(100, (Math.abs(this.stateData.maxLeftTwistAngle) / 60) * 100);
          this.stateData.maxLeftTwistAngle = 0;
        }
        
        const metrics = this.computeMetrics(results.landmarks, results.worldLandmarks, timestamp);
        const controlScore = Math.min(100, Math.max(0, 100 - (metrics.twistVelocity > 180 ? (metrics.twistVelocity - 180) / 5 : 0)));
        
        this.stateData.repFormScore = Math.round((rangeScore * 0.7) + (controlScore * 0.3));
        this.stateData.formScore = this.stateData.repFormScore;
        
        // CRITICAL: Store per-rep data for session analytics
        // Use rangeScore as the ROM for this specific rep (not rolling average)
        this.repHistory.push({
          rep: this.repetitionCount,
          formScore: this.stateData.repFormScore,
          rom: Math.round(rangeScore), // ROM achieved in THIS rep
          timestamp: timestamp
        });
        
        // console.log(`FSM: Stored rep #${this.repetitionCount} - Form: ${this.stateData.repFormScore}, ROM: ${Math.round(rangeScore)}`);
        
        // Mark as counted and start cooldown
        this.stateData.repCounted = true;
        this.stateData.lastSideCompleted = countSide;
        this.stateData.lastRepTime = timestamp;
        this.stateData.inInternalCooldown = true;
        this.stateData.cooldownEndTime = timestamp + INTERNAL_COOLDOWN;
        isRepCompleted = true;
      }
      
      // Mark as stable in center position
      this.stateData.stableInCenter = true;
      
      // Reset visited states to prevent old history from affecting new reps
      this.stateData.visitedStates = ['CENTER'];
    }
    
    // Compose the final result
    return {
      ...baseResult,
      isRepCompleted,
      repetitionCount: this.repetitionCount
    };
  }
  
  computeMetrics(landmarks, worldLandmarks, timestamp) {
    // Skip processing if key landmarks are missing
    if (!worldLandmarks || !worldLandmarks[11] || !worldLandmarks[12] || 
        !worldLandmarks[23] || !worldLandmarks[24]) {
      return {};
    }
    
    const leftShoulder = worldLandmarks[11];
    const rightShoulder = worldLandmarks[12];
    const leftHip = worldLandmarks[23];
    const rightHip = worldLandmarks[24];
    
    // Calculate the rotation angle between shoulders and hips
    // This measures how twisted the torso is using 3D rotation (X + Z axes)
    const shoulderVector = {
      x: rightShoulder.x - leftShoulder.x,
      z: rightShoulder.z - leftShoulder.z
    };
    
    const hipVector = {
      x: rightHip.x - leftHip.x,
      z: rightHip.z - leftHip.z
    };
    
    const shoulderAngle = Math.atan2(shoulderVector.z, shoulderVector.x);
    const hipAngle = Math.atan2(hipVector.z, hipVector.x);
    let rotationAngle = shoulderAngle - hipAngle;
    
    // Convert to degrees and normalize
    rotationAngle = rotationAngle * (180 / Math.PI);
    if (rotationAngle > 180) rotationAngle -= 360;
    if (rotationAngle < -180) rotationAngle += 360;
    
    // Apply smoothing with exponential moving average
    const smoothingFactor = 0.3;
    if (this.stateData.currentTwistAngle === undefined) {
      this.stateData.currentTwistAngle = rotationAngle;
    } else {
      this.stateData.currentTwistAngle = (this.stateData.currentTwistAngle * (1 - smoothingFactor)) + 
                                        (rotationAngle * smoothingFactor);
    }
    
    // Save previous angle for tracking
    this.stateData.previousTwistAngle = this.stateData.currentTwistAngle;
    
    // Update twist history for velocity calculation
    if (!this.stateData.twistHistory) {
      this.stateData.twistHistory = [];
    }
    
    if (this.stateData.twistHistory.length > 10) {
      this.stateData.twistHistory.shift();
    }
    this.stateData.twistHistory.push({angle: this.stateData.currentTwistAngle, time: timestamp});
    
    // Calculate twist velocity
    let twistVelocity = 0;
    if (this.stateData.twistHistory.length >= 2) {
      const first = this.stateData.twistHistory[0];
      const last = this.stateData.twistHistory[this.stateData.twistHistory.length - 1];
      const timeDiff = (last.time - first.time) / 1000;
      if (timeDiff > 0) {
        const angleDiff = Math.abs(last.angle - first.angle);
        twistVelocity = angleDiff / timeDiff;
      }
    }
    
    // Update max twist angles based on current state
    if (this.currentState === CoreTwistState.RIGHT && this.stateData.currentTwistAngle > this.stateData.maxRightTwistAngle) {
      this.stateData.maxRightTwistAngle = this.stateData.currentTwistAngle;
    } else if (this.currentState === CoreTwistState.LEFT && this.stateData.currentTwistAngle < this.stateData.maxLeftTwistAngle) {
      this.stateData.maxLeftTwistAngle = this.stateData.currentTwistAngle;
    }

    // NEW: Track overall max twist angle for ROM calculation
    const currentAbsTwist = Math.abs(this.stateData.currentTwistAngle);
    if (currentAbsTwist > this.stateData.maxAbsoluteTwistAngle) {
      this.stateData.maxAbsoluteTwistAngle = currentAbsTwist;
    }
    
    // Detect current position
    const isCentered = Math.abs(this.stateData.currentTwistAngle) < CENTER_THRESHOLD;
    const isTwistedRight = this.stateData.currentTwistAngle > TWIST_THRESHOLD;
    const isTwistedLeft = this.stateData.currentTwistAngle < -TWIST_THRESHOLD;
    
    // Determine twist direction
    let twistDirection = 'center';
    if (isTwistedRight) {
      twistDirection = 'right';
    } else if (isTwistedLeft) {
      twistDirection = 'left';
    }
    
    // Calculate form quality scores
    const rangeOfMotionScore = Math.min(100, Math.max(0, (Math.abs(this.stateData.currentTwistAngle) / 60) * 100));
    const controlScore = Math.min(100, Math.max(0, 100 - (twistVelocity > 180 ? (twistVelocity - 180) / 5 : 0)));

    // NEW: Calculate ROM based on maximum twist achieved
    // Target is 60 degrees for full ROM
    const rangeOfMotion = Math.min(100, (this.stateData.maxAbsoluteTwistAngle / 60) * 100);

    // Calculate exercise progress (0-100%)
    // 0% = center, 50% = fully twisted right, -50% = fully twisted left
    let exerciseProgress = 0;
    if (this.stateData.currentTwistAngle > 0) {
      exerciseProgress = Math.min(50, (this.stateData.currentTwistAngle / 60) * 50);
    } else {
      exerciseProgress = Math.max(-50, (this.stateData.currentTwistAngle / 60) * 50);
    }

    // Convert to 0-100 scale for UI
    exerciseProgress = 50 + exerciseProgress;

    return {
      currentTwistAngle: this.stateData.currentTwistAngle,
      previousTwistAngle: this.stateData.previousTwistAngle,
      twistDirection,
      twistVelocity,

      // Position flags
      isCentered,
      isTwistedRight,
      isTwistedLeft,

      // Form metrics
      rangeOfMotionScore,
      rangeOfMotion,  // NEW: Overall ROM based on max achieved
      controlScore,
      exerciseProgress
    };
  }
  
  updateFormScores(metrics, transitioned) {
    if (metrics.rangeOfMotionScore === undefined) return;

    // Update individual form scores
    this.formScores.control = metrics.controlScore;

    // ROM: Always show CURRENT/REAL-TIME ROM for live feedback
    // Use rangeOfMotion (max achieved) if available, otherwise current position score
    if (metrics.rangeOfMotion !== undefined) {
      this.formScores.rangeOfMotion = Math.round(metrics.rangeOfMotion);
    } else if (metrics.rangeOfMotionScore !== undefined) {
      this.formScores.rangeOfMotion = Math.round(metrics.rangeOfMotionScore);
    }

    // Calculate and update overall form score
    if (this.stateData.repFormScore) {
      this.formScores.overall = Math.round(this.formScores.overall * 0.7 + this.stateData.repFormScore * 0.3);
      this.stateData.repFormScore = 0;  // Reset after use so next frame uses current metrics
    } else {
      // If no rep has been completed yet, use current metrics
      const currentFormScore = Math.round(
        (metrics.rangeOfMotionScore * 0.7) +
        (metrics.controlScore * 0.3)
      );
      this.formScores.overall = Math.round(this.formScores.overall * 0.7 + currentFormScore * 0.3);
    }
  }
  
  generateFormFeedback(metrics, currentState) {
    if (!metrics.currentTwistAngle) return null;
    
    // Form feedback based on metrics
    if (Math.abs(metrics.currentTwistAngle) > MAX_TWIST_ANGLE) {
      return "Reduce twist range for safety";
    }
    
    if (metrics.twistVelocity > 300) {
      return "Slow down for better control";
    }
    
    if (currentState === CoreTwistState.RIGHT || currentState === CoreTwistState.LEFT) {
      if (metrics.rangeOfMotionScore < 70) {
        return "Twist further for better range";
      }
    }
    
    return null;
  }
  
  reset() {
    // Reset to initial state
    this.currentState = this.constructor.initialConfig.initialState;
    this.stateData = { ...this.constructor.initialConfig.initialStateData };
    this.formScores = { ...this.constructor.initialConfig.initialFormScores };
    this.repetitionCount = 0;
    this.lastTransitionTime = 0;
    this.formFeedback = null;
  }
} 